import { APICore } from './apiCore';
import { AppMode, getAppMode } from '../../config/runtimeSettings';

const api = new APICore();

export type SubscriptionPlan = {
    _id: string;
    name: string;
    description?: string;
    billingInterval: 'day' | 'week' | 'month' | 'year';
    status: 'active' | 'inactive' | 'cancelled' | 'incomplete';
    currency: string;
    amount: number;
    mode: AppMode;
    stripePriceId?: string;
    stripeProductId?: string;
    createdAt?: string;
    updatedAt?: string;
};

export type FranchiseSubscription = {
    _id: string;
    franchiseId: string;
    subscriptionPlanId: string | SubscriptionPlan;
    mode: AppMode;
    stripeSubscriptionId?: string;
    stripeCustomerId: string;
    currentPeriodEnd?: string;
    cancelledAtPeriodEnd?: boolean;
    cancelledAt?: string | null;
    status: 'active' | 'inactive' | 'cancelled' | 'incomplete';
    amount?: number;
    isPaid?: boolean;
};

/**
 * Returns the current franchise subscription for the logged-in franchise admin.
 * If the franchise is not subscribed yet, this resolves to null.
 */
export const getMySubscription = async (): Promise<FranchiseSubscription | null> => {
    try {
        const res = await api.get('/subscriptions/me', {});
        const data = (res as any)?.data ?? res;
        if (!data) return null;
        return data as FranchiseSubscription;
    } catch (err) {
        // Treat errors (e.g. 404 Not Found) as \"no active subscription\"
        return null;
    }
};

/**
 * Lists subscription plans available in the current app mode for franchise admins.
 */
export const getAvailablePlans = async (): Promise<SubscriptionPlan[]> => {
    const mode: AppMode = getAppMode();
    const res = await api.get('/subscriptions/plans', { mode });
    const data = (res as any)?.data ?? res;
    if (!data) return [];
    return Array.isArray(data) ? (data as SubscriptionPlan[]) : [];
};

/**
 * Starts a subscription checkout for the given plan and returns a Stripe PaymentIntent client secret.
 * The client secret is used on the frontend with Stripe.js to collect and confirm card details.
 */
export const startSubscriptionCheckout = async (
    subscriptionPlanId: string
): Promise<{ clientSecret: string }> => {
    const res = await api.create('/subscriptions/checkout', { subscriptionPlanId });
    const data = (res as any)?.data ?? res;
    return data as { clientSecret: string };
};

/**
 * Confirm a subscription after the card has been set up with Stripe.
 * Expects the backend to create the Stripe subscription and return the updated FranchiseSubscription.
 */
export const confirmSubscription = async (
    subscriptionPlanId: string,
    paymentMethodId: string
): Promise<FranchiseSubscription | null> => {
    try {
        const res = await api.create('/subscriptions/confirm', {
            subscriptionPlanId,
            paymentMethodId,
        });
        const data = (res as any)?.data ?? res;
        return data as FranchiseSubscription;
    } catch {
        return null;
    }
};

/**
 * Sync the current franchise subscription with Stripe and return the updated record.
 * Call this after a successful payment so the backend marks the subscription as active.
 */
export const syncCurrentSubscription = async (): Promise<FranchiseSubscription | null> => {
    try {
        const res = await api.create('/subscriptions/sync', {});
        const data = (res as any)?.data ?? res;
        return data as FranchiseSubscription;
    } catch {
        return null;
    }
};

/**
 * Cancel the current franchise subscription.
 * This schedules cancellation at period end and updates the database.
 */
export const cancelSubscription = async (): Promise<FranchiseSubscription | null> => {
    try {
        const res = await api.delete('/subscriptions/cancel');
        const data = (res as any)?.data ?? res;
        return data as FranchiseSubscription;
    } catch {
        return null;
    }
};

